import { ChangeEvent, useEffect, useRef, useState } from "react";
import { InputFieldType } from "shared/types/FormFieldTypes";
import FloatingInputWrapper from "shared/components/layouts/FloatingInputWrapper";
import { FormFieldEnums } from "shared/types/enums";
import IconButton from "../buttons/IconButton";
import "styles/components/forms/inputs/textInput.css";

type TextInputFieldType = InputFieldType & {
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  iconName?: string;
  autoComplete?: string;
  showPasswordToggle?: boolean;
  styleProps?: React.CSSProperties;
  name?: string;
  fixCordova?: boolean;
};

const TextInput = ({
  id,
  type,
  placeholder,
  className = "",
  required,
  value,
  onChange,
  iconName,
  autoComplete,
  showPasswordToggle,
  styleProps,
  name,
  fixCordova = false,
}: TextInputFieldType) => {
  const [fieldValue, setFieldValue] = useState<string>(value ? value : "");
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setFieldValue(value ?? "");
  }, [value]);

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e);
    }
    setFieldValue(e.target.value);
  };

  const finalInputType =
    type === "password" && showPasswordToggle
      ? isPasswordVisible
        ? "text"
        : "password"
      : type;

  const handlePasswordToggle = () => {
    setIsPasswordVisible((prev) => {
      const nextState = !prev;

      // Only apply the masking fix if Cordova workaround is active
      if (fixCordova && inputRef.current) {
        forceMaskFix(inputRef.current);
      }

      return nextState;
    });
  };

  const forceMaskFix = (el: HTMLInputElement) => {
    const val = el.value;
    el.value = ""; // temporarily clear
    el.value = val; // reassign to force redraw
  };

  return (
    <FloatingInputWrapper
      styleProps={styleProps}
      id={id}
      placeholder={placeholder}
      shrink={fieldValue ? true : false}
      iconName={iconName}
    >
      <input
        value={fieldValue}
        className={`${className} ${
          !isPasswordVisible && fixCordova ? "hideText" : ""
        }`}
        type={fixCordova ? "text" : finalInputType}
        onChange={handleOnChange}
        required={required}
        name={name ? name : id}
        id={id}
        autoComplete={autoComplete}
        ref={inputRef}
      />
      {showPasswordToggle && type === FormFieldEnums.Password && (
        <div className="password-eye-container">
          <IconButton
            svgProp={{ width: 18, height: 18 }}
            iconName={isPasswordVisible ? "EyeSlash" : "Eye"}
            id={id + (isPasswordVisible ? "EyeSlash" : "Eye")}
            onClickHandler={handlePasswordToggle}
          />
        </div>
      )}
    </FloatingInputWrapper>
  );
};
export default TextInput;
